import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { useBodyScrollLock } from "@/hooks/useBodyScrollLock";
import { reportNinjaIbvSessionStep } from "@/lib/api/bankService";
import type { NinjaIframePopupProps } from "./types";

type NinjaIframePopupMessage =
  | {
      onSuccess?: {
        ibv_report_status?: string;
        product_request_id?: string;
        request_id?: string;
        [key: string]: unknown;
      };
      onError?: { message?: string; [key: string]: unknown };
      message?: string;
      [key: string]: unknown;
    }
  | {
      type?: string;
      metadata?: {
        step?: string;
        status?: string;
        product_request_id?: string;
        request_id?: string;
        [key: string]: unknown;
      };
      [key: string]: unknown;
    };

/** Merged handler view — postMessage payloads may include V1 or V2 fields. */
interface NinjaIframeHandlerData {
  onSuccess?: {
    ibv_report_status?: string;
    product_request_id?: string;
    request_id?: string;
    [key: string]: unknown;
  };
  onError?: { message?: string; [key: string]: unknown };
  message?: string;
  type?: string;
  metadata?: {
    step?: string;
    status?: string;
    product_request_id?: string;
    request_id?: string;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

const V2_SUCCESS_STATUSES = ["success", "complete"];

/**
 * Ninja IBV iframe popup — mirrors Angular `NinjaPopupComponent`.
 * Forwards iframe notification steps and records popup open/close to
 * POST /api/ninja/ibv_session_steps/:applicantId for DB storage.
 */
export function NinjaIframePopup({
  applicantId,
  isOpen,
  onClose,
  ninjaVersion,
  responseData,
  isLoading = false,
  onSuccess,
}: NinjaIframePopupProps) {
  const [errorMessage, setErrorMessage] = useState<string>("");
  const closeReportedRef = useRef(false);
  useBodyScrollLock(isOpen);

  const productRequestId = responseData?.product_request_id ?? "";

  const reportSessionStep = useCallback(
    (metadata: Record<string, unknown>, type = "notification") => {
      if (!productRequestId) return;
      void reportNinjaIbvSessionStep(applicantId, {
        product_request_id: productRequestId,
        type,
        metadata,
      });
    },
    [applicantId, productRequestId],
  );

  const reportPopupOpened = useCallback(() => {
    reportSessionStep({
      status: "popup open",
      step: "popup_open",
      timestamp: Math.floor(Date.now() / 1000),
    });
  }, [reportSessionStep]);

  const reportPopupClosed = useCallback(() => {
    if (closeReportedRef.current) return;
    closeReportedRef.current = true;
    reportSessionStep({
      status: "popup closed",
      step: "popup_closed",
      timestamp: Math.floor(Date.now() / 1000),
    });
  }, [reportSessionStep]);

  const handleClose = useCallback(() => {
    reportPopupClosed();
    onClose();
  }, [onClose, reportPopupClosed]);

  const iframeSrc = useMemo(() => {
    const url = responseData?.ibv_connection_link;
    if (!url) return "";
    return url;
  }, [responseData?.ibv_connection_link]);

  useEffect(() => {
    if (!isOpen) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: clear the error message each time the popup opens
    setErrorMessage("");
    closeReportedRef.current = false;
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    reportPopupOpened();
  }, [isOpen, reportPopupOpened]);

  useEffect(() => {
    if (!isOpen) return;

    const handler = (event: MessageEvent) => {
      try {
        const data = event.data as NinjaIframePopupMessage;
        if (!data) return;

        const msgData = data as NinjaIframeHandlerData;
        const onErr = msgData.onError;
        const onOk = msgData.onSuccess;

        if (onErr?.message) {
          setErrorMessage(msgData.message || onErr.message);
          return;
        }

        const isV2 = ninjaVersion === "V2";

        if (isV2) {
          // Persist every iframe notification step (Ninja posts these during the V2 flow).
          if (msgData.type === "notification" && msgData.metadata != null) {
            console.log("[NinjaIframePopup] notification event", {
              type: msgData.type,
              metadata: msgData.metadata,
            });
            const stepProductRequestId =
              productRequestId || msgData.metadata.product_request_id || "";
            if (stepProductRequestId) {
              void reportNinjaIbvSessionStep(applicantId, {
                product_request_id: stepProductRequestId,
                type:
                  typeof msgData.type === "string"
                    ? msgData.type
                    : "notification",
                metadata: msgData.metadata as Record<string, unknown>,
              });
            }
          }

          if (
            msgData.type === "notification" &&
            msgData.metadata?.step === "callback"
          ) {
            const status =
              typeof msgData.metadata?.status === "string"
                ? msgData.metadata.status.toLowerCase()
                : "";
            if (status && V2_SUCCESS_STATUSES.includes(status)) {
              // Automated close after successful bank link callback
              reportPopupClosed();
              onSuccess({
                product_request_id:
                  responseData?.product_request_id ||
                  msgData.metadata?.product_request_id,
                request_id: msgData.metadata?.request_id,
              });
            }
            return;
          }
        } else {
          // V1 style success (Angular behavior)
          if (onOk?.ibv_report_status === "initiated") {
            onSuccess({
              product_request_id:
                responseData?.product_request_id || onOk?.product_request_id,
              request_id: onOk?.request_id,
            });
          }
        }
      } catch (e: unknown) {
        const errorMessage =
          e instanceof Error
            ? e.message
            : e &&
                typeof e === "object" &&
                "message" in e &&
                typeof (e as { message: unknown }).message === "string"
              ? (e as { message: string }).message
              : "Failed to process bank link response";
        setErrorMessage(errorMessage || "Failed to process bank link response");
      }
    };

    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [
    applicantId,
    isOpen,
    onSuccess,
    productRequestId,
    reportPopupClosed,
    responseData?.product_request_id,
    ninjaVersion,
  ]);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-[calc(100vw-24px)] sm:max-w-3xl w-full max-h-[90vh] flex flex-col relative safe-area-bottom">
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleClose();
          }}
          className="absolute top-4 right-4 z-[100] w-8 h-8 flex items-center justify-center text-gray-400 hover:bg-gray-100 rounded-full transition-colors cursor-pointer"
          type="button"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center justify-center p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            Link Your Bank Account
          </h2>
        </div>

        <div className="flex-1 overflow-auto p-6">
          {(isLoading || !iframeSrc) && !errorMessage && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Loading bank linking...</span>
            </div>
          )}

          {iframeSrc ? (
            <iframe
              src={iframeSrc}
              title="Link your bank account"
              className="w-full min-h-[700px] border-0"
            />
          ) : null}

          {!!errorMessage && (
            <p className="text-red-600 mt-3 text-sm">{errorMessage}</p>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
